import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Button
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { usePartnership } from '../contexts/PartnershipContext';
import { PartnershipScenario, CalculatedResults } from '../types/types';

const ComparisonView: React.FC = () => {
    const { scenarios, compareScenarios } = usePartnership();
    const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
    const [comparisonData, setComparisonData] = useState<Array<{
        scenario: PartnershipScenario;
        results: CalculatedResults;
    }>>([]);

    const handleScenarioSelect = (event: SelectChangeEvent<string[]>) => {
        let selected = event.target.value as string[];
        if (selected.length > 3) selected = selected.slice(0, 3);
        setSelectedScenarios(selected);
        if (selected.length > 0) {
            const comparison = compareScenarios(selected);
            setComparisonData(comparison);
        } else {
            setComparisonData([]);
        }
    };

    const createTop3Comparison = () => {
        const ranked = scenarios
            .map(s => ({ scenario: s, results: compareScenarios([s.id])[0].results }))
            .sort((a, b) => b.results.riskAdjustedROIPercentage - a.results.riskAdjustedROIPercentage)
            .slice(0, 3);
        const ids = ranked.map(r => r.scenario.id);
        setSelectedScenarios(ids);
        setComparisonData(ranked);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercentage = (value: number) => {
        return `${value.toFixed(2)}%`;
    };

    const getRecommendation = () => {
        if (comparisonData.length === 0) return null;

        const bestROI = comparisonData.reduce((best, current) => {
            return current.results.riskAdjustedROIPercentage > best.results.riskAdjustedROIPercentage ? current : best;
        }, comparisonData[0]);

        return (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="h6" color="white">
                    Recommended Partnership:
                </Typography>
                <Typography color="white">
                    {bestROI.scenario.name} ({bestROI.scenario.partnerType})
                    with a risk-adjusted ROI of {formatPercentage(bestROI.results.riskAdjustedROIPercentage)}
                </Typography>
            </Box>
        );
    };

    const comparisonChartData = {
        labels: comparisonData.map(d => d.scenario.name),
        datasets: [
            {
                label: 'Total Investment',
                data: comparisonData.map(d => d.results.totalInvestment),
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
            },
            {
                label: 'Total Returns',
                data: comparisonData.map(d => d.results.totalReturns),
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
            },
            {
                label: 'Net Present Value',
                data: comparisonData.map(d => d.results.netPresentValue),
                backgroundColor: 'rgba(153, 102, 255, 0.5)',
            }
        ]
    };

    const exportComparison = () => {
        const rows = [
            ['Scenario', ...comparisonData.map(d => d.scenario.name)],
            ['Partner Type', ...comparisonData.map(d => d.scenario.partnerType)],
            ['Total Investment', ...comparisonData.map(d => d.results.totalInvestment)],
            ['Total Returns', ...comparisonData.map(d => d.results.totalReturns)],
            ['Base ROI %', ...comparisonData.map(d => d.results.baseROIPercentage)],
            ['Risk-Adjusted ROI %', ...comparisonData.map(d => d.results.riskAdjustedROIPercentage)],
            ['Break-even (months)', ...comparisonData.map(d => d.results.breakEvenMonths)],
            ['NPV', ...comparisonData.map(d => d.results.netPresentValue)],
        ];
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'comparison.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Paper elevation={3} sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom>Partnership Comparison</Typography>
                </Grid>

                {/* Scenario Selection */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Select Scenarios to Compare</InputLabel>
                        <Select
                            multiple
                            value={selectedScenarios}
                            onChange={handleScenarioSelect}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const scenario = scenarios.find(s => s.id === value);
                                        return scenario ? (
                                            <Chip
                                                key={value}
                                                label={scenario.name}
                                                size="small"
                                            />
                                        ) : null;
                                    })}
                                </Box>
                            )}
                        >
                            {scenarios.map((scenario) => (
                                <MenuItem key={scenario.id} value={scenario.id}>
                                    {scenario.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                        <Chip label="Max 3 scenarios" size="small" />
                        <Button variant="outlined" size="small" onClick={createTop3Comparison}>Create Comparison</Button>
                    </Box>
                </Grid>

                {comparisonData.length > 0 && (
                    <>
                        {/* Scenario Cards */}
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                {comparisonData.map(d => {
                                    const bestRiskAdj = Math.max(...comparisonData.map(x => x.results.riskAdjustedROIPercentage));
                                    const bestPayback = Math.min(...comparisonData.map(x => x.results.breakEvenMonths));
                                    const isBestROI = d.results.riskAdjustedROIPercentage === bestRiskAdj;
                                    const isBestPayback = d.results.breakEvenMonths === bestPayback;
                                    const mdCols = comparisonData.length === 3 ? 4 : (comparisonData.length === 2 ? 6 : 12);
                                    return (
                                        <Grid item xs={12} sm={12} md={mdCols} key={d.scenario.id}>
                                            <Paper elevation={3} sx={{ p: 2, borderLeft: isBestROI ? '6px solid #4ECDC4' : '6px solid transparent' }}>
                                                <Typography variant="h6" sx={{ mb: 1 }}>{d.scenario.name}</Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>{d.scenario.partnerType}</Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip label={`Base ROI ${d.results.baseROIPercentage.toFixed(1)}%`} />
                                                    <Chip color={isBestROI ? 'success' : 'default'} label={`Risk-Adj ROI ${d.results.riskAdjustedROIPercentage.toFixed(1)}%`} />
                                                    <Chip color={isBestPayback ? 'success' : 'default'} label={`Payback ${d.results.breakEvenMonths} mo`} />
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                        {/* Comparison Table */}
                        <Grid item xs={12}>
                            <TableContainer>
                                <Table aria-label="comparison table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Metric</TableCell>
                                            {comparisonData.map(d => (
                                                <TableCell key={d.scenario.id}>{d.scenario.name}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Partner Type</TableCell>
                                            {comparisonData.map(d => (
                                                <TableCell key={d.scenario.id}>{d.scenario.partnerType}</TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Total Investment</TableCell>
                                            {(() => {
                                                const minVal = Math.min(...comparisonData.map(d => d.results.totalInvestment));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.totalInvestment === minVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {formatCurrency(d.results.totalInvestment)}
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Total Returns</TableCell>
                                            {(() => {
                                                const maxVal = Math.max(...comparisonData.map(d => d.results.totalReturns));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.totalReturns === maxVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {formatCurrency(d.results.totalReturns)}
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>ROI</TableCell>
                                            {(() => {
                                                const maxVal = Math.max(...comparisonData.map(d => d.results.baseROIPercentage));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.baseROIPercentage === maxVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {formatPercentage(d.results.baseROIPercentage)}
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Break-even Period</TableCell>
                                            {(() => {
                                                const minVal = Math.min(...comparisonData.map(d => d.results.breakEvenMonths));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.breakEvenMonths === minVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {d.results.breakEvenMonths} months
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Net Present Value</TableCell>
                                            {(() => {
                                                const maxVal = Math.max(...comparisonData.map(d => d.results.netPresentValue));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.netPresentValue === maxVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {formatCurrency(d.results.netPresentValue)}
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Risk-adjusted ROI</TableCell>
                                            {(() => {
                                                const maxVal = Math.max(...comparisonData.map(d => d.results.riskAdjustedROIPercentage));
                                                return comparisonData.map(d => (
                                                    <TableCell key={d.scenario.id} sx={{ bgcolor: d.results.riskAdjustedROIPercentage === maxVal ? 'rgba(78,205,196,0.15)' : undefined }}>
                                                        {formatPercentage(d.results.riskAdjustedROIPercentage)}
                                                    </TableCell>
                                                ));
                                            })()}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        {/* Comparison Chart */}
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Financial Comparison</Typography>
                                <Box sx={{ height: 400 }}>
                                    <Bar
                                        data={comparisonChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                title: {
                                                    display: true,
                                                    text: 'Investment, Returns, and NPV Comparison'
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button variant="outlined" onClick={exportComparison} aria-label="Export comparison to CSV">Export Comparison</Button>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Recommendation */}
                        <Grid item xs={12}>
                            {getRecommendation()}
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    );
};

export default ComparisonView; 